import React, { useEffect, useMemo, useState } from "react";

import { Outlet, useLocation, useMatches, useNavigate } from "react-router";

import { PermissionProvider } from "@/components/context/PermissionContext";
import { TenantDisciplinesProvider } from "@/components/context/TenantDisciplinesContext";
import { OverviewLoading } from "@/components/overview/OverviewLoading";
import { profileForPlatformSession } from "@/components/shared/presentation-profile";
import { BUILDER_SURFACES, programmeRoleFor } from "@/components/shared/programme-role";
import { AppShell, Button, PageContainer } from "@/components/ui";
import {
  type AssignedModuleSummary,
  EDITABLE_WORKFLOW_STATES,
  type PlatformAdminOverview,
  type PortfolioSummary,
  type ProgrammeSummary,
  useEditorProgrammeSyncController,
  usePlatformSession,
  type UserSummary,
} from "@/platform";
import { PublishedReadinessProvider } from "@/platform/editor-readiness-context";
import { RealtimeBoundary, useRealtimePresence } from "@/platform/realtime-context";
import { describeSessionError } from "@/platform/session-error";
import { useUnloadGuard } from "@/router/departure-guard";
import { programmeAddress } from "@/router/programme-editor/ProgrammeSkeleton";
import { type WorkspaceOutletContext, WorkspaceProvider } from "@/router/workspace-context";
import { type AuthorizationSubject, can } from "@/server/authorization";
import type { NotificationFeedItem } from "@/server/types";
import type { ProviderSettings } from "@/types/provider";
import { begin } from "@/utils/begin";
import { type RegionPreference, resolveRegion } from "@/utils/format-instant";
import { applyTextSize } from "@/utils/text-size";

import { useAuthShell } from "./auth-shell-context";
import { AuthScreen } from "./AuthScreen";
import { OpenProgrammeSidebar } from "./ProgrammeSidebar";
import { RegionBoundary } from "./RegionBoundary";
import { RouteTitle } from "./RouteTitle";
import { ProgrammeSidebarSkeleton, RouteContentSkeleton, ShellSkeleton } from "./ShellSkeleton";
import { useViewOnlyProgramme } from "./useViewOnlyProgramme";
import { WorkspaceSidebar } from "./WorkspaceSidebar";
import { WorkspaceTopbar } from "./WorkspaceTopbar";

/** What the sidebar names for a platform admin, whose session tenant is incidental. */
const PLATFORM_SCOPE_NAME = "SocratIQ Platform";

/** Everything the shell loads for one tenant, tagged with the tenant it is for. */
interface PlatformWorkspaceData {
  tenantId: string | null;
  programmes: ProgrammeSummary[];
  modules: AssignedModuleSummary[];
  users: UserSummary[];
  portfolio: PortfolioSummary | null;
  disciplines: readonly string[];
  allowLeadTeamManagement: boolean;
  /** The tenant's own locale and zone, which a person may override. */
  tenantRegion: RegionPreference;
  tenantProviderSettings: ProviderSettings | null;
  adminOverview: PlatformAdminOverview | null;
  error: Error | null;
}

/**
 * Also what a stale or unresolved record reads as. Lead team management defaults
 * to allowed, matching the platform-admin branch, which has no provider settings
 * to say otherwise.
 */
const NO_WORKSPACE: PlatformWorkspaceData = {
  tenantId: null,
  programmes: [],
  modules: [],
  users: [],
  portfolio: null,
  disciplines: [],
  allowLeadTeamManagement: true,
  tenantRegion: {},
  tenantProviderSettings: null,
  adminOverview: null,
  error: null,
};

/** A destination resolved against this origin, or null when it does not parse. */
function parseInApp(actionUrl: string): URL | null {
  // Not `URL.canParse`, which Safari before 17 and Chrome before 120 lack.
  try {
    return new URL(actionUrl, window.location.origin);
  } catch {
    return null;
  }
}

/**
 * The persistent authenticated shell: header, top-level nav, and the routed
 * content area. This is the `ProtectedLayout` route element — everything
 * below the top nav is whatever the router matched, rendered through
 * `<Outlet>`, with shared platform/session/editor state passed down as route
 * context (see {@link WorkspaceOutletContext}) rather than duplicated.
 */
export function AuthenticatedWorkspace() {
  const { profileId, onLogout, onSwitchUser } = useAuthShell();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    client,
    session: platformSession,
    status: sessionStatus,
    error: sessionError,
    refreshSession,
  } = usePlatformSession();
  // Read from the session rather than inferred from how this bundle was built.
  // The API is the only thing that knows whether it enforces leases, and a
  // client guessing from its own build configuration is right only while the
  // two happen to agree. Defaults to enabled, including before the session
  // resolves: skipping a lease the server enforces means two people editing
  // one programme with no lock, which is worse than a redundant round trip.
  const editLeasesEnabled = platformSession?.editLeasesEnabled ?? true;
  const editorSync = useEditorProgrammeSyncController(client, 100, editLeasesEnabled);
  // Here rather than in the editor: work a departure could not send outlives it.
  useUnloadGuard(() => editorSync.session.pending);
  // One record rather than nine states, tagged with the tenant it was loaded
  // for. Anything holding a different tenant is stale by definition, so the
  // loading, empty and error states below fall out of a comparison instead of
  // needing a synchronous clear every time the tenant moves.
  const [workspaceData, setWorkspaceData] = useState<PlatformWorkspaceData>(NO_WORKSPACE);
  const subject = useMemo<AuthorizationSubject>(
    () =>
      platformSession?.subject ?? {
        userId: profileId,
        accountRole: "deactivated",
        responsibilities: [],
      },
    [platformSession, profileId],
  );
  const isPlatformAdmin = subject.accountRole === "platform-admin";
  const currentTenantId = platformSession?.tenant.id ?? null;

  // A record naming another tenant is stale, and so is one naming this tenant
  // before the session is ready to have asked for it.
  const workspaceReady =
    currentTenantId !== null &&
    sessionStatus === "ready" &&
    workspaceData.tenantId === currentTenantId;
  const workspace = workspaceReady ? workspaceData : NO_WORKSPACE;
  const platformDisciplines = workspace.disciplines;
  const allowLeadTeamManagement = workspace.allowLeadTeamManagement;
  const region = useMemo(
    () => resolveRegion(workspace.tenantRegion, platformSession?.user),
    [workspace.tenantRegion, platformSession?.user],
  );

  const activeProfile = profileForPlatformSession(platformSession, profileId);

  // One presence connection per tab, above every route that shows a programme,
  // so moving between the programme editor and a module neither reconnects nor
  // changes who peers see. The deepest match names what is open. A builder on
  // the programme editor sees a refusal card, so is kept out of that room.
  const matches = useMatches();
  const { programmeId: routeProgrammeId, moduleId: routeModuleId } = (matches[matches.length - 1]
    ?.params ?? {}) as { programmeId?: string; moduleId?: string };
  const programmeShell = matches.some(
    (match) => (match.handle as { programmeShell?: boolean } | undefined)?.programmeShell,
  );
  const presenceProgrammeId =
    routeProgrammeId &&
    (routeModuleId || programmeRoleFor(platformSession, routeProgrammeId) !== "builder")
      ? routeProgrammeId
      : null;
  // A builder writes modules through the module path; the programme's own
  // content is its lead's, whatever `programme:write` admits.
  const builderHere = programmeRoleFor(platformSession, editorSync.record?.id) === "builder";
  const canEditProgramme =
    !builderHere &&
    can(subject, "programme:write", {
      type: "programme",
      programmeId: editorSync.record?.id ?? "current",
    });
  const canEditWorkflowState = EDITABLE_WORKFLOW_STATES.has(editorSync.record?.workflowState);
  const canEditCurrentProgramme = canEditProgramme && canEditWorkflowState;
  const viewMode = useViewOnlyProgramme(routeProgrammeId);
  // "locked" is deliberately not here. It now means the structural lease is
  // held elsewhere, which read-onlys the structural step alone. Inerting the
  // whole shell locked a lead out of narrative and every module step too.
  const shouldRenderReadOnlyEditorView =
    editorSync.status === "read-only" || !canEditCurrentProgramme || viewMode.viewingProgramme;

  // Below the read-only computation, so presence reports whether this editor
  // can actually change anything rather than which route it is on.
  const presence = useRealtimePresence(client, presenceProgrammeId, editorSync.clientId, {
    name: platformSession?.user.displayName ?? platformSession?.user.email ?? "Another editor",
    userId: platformSession?.user.id,
    moduleId: routeModuleId,
    activity: shouldRenderReadOnlyEditorView ? "viewing" : "editing",
  });
  const canManageTenant = can(subject, "assignment:manage", { type: "tenant" });
  const canAccessWorkspace = activeProfile.role !== "platform-admin";
  const canQuickCreateProgramme = can(subject, "programme:create", { type: "tenant" });
  const hasProgrammeLeadResponsibility = subject.responsibilities.some(
    (assignment) => assignment.responsibility === "lead",
  );
  const canQuickCreateModule = activeProfile.role === "builder" || hasProgrammeLeadResponsibility;
  const canManageProgrammeTeam = hasProgrammeLeadResponsibility && allowLeadTeamManagement;
  const canViewReports = activeProfile.role === "client-admin";

  // Fetch and publish. Every update lands in a continuation, so the effect
  // below can call it on mount without cascading a second render, and there is
  // no synchronous clear on the way in: the record carries the tenant it
  // belongs to, and the derivations below discard anything that names another.
  const fetchPlatformWorkspace = React.useCallback((): Promise<void> => {
    const tenantId = currentTenantId;
    if (!tenantId) {
      return Promise.resolve();
    }

    // A failed re-read keeps what the last one read: emptying every list
    // blanked the screens reading them, a builder's studio among them.
    const failed = (cause: unknown): void => {
      const error = cause instanceof Error ? cause : new Error("Unable to load platform workspace");
      setWorkspaceData((current) =>
        current.tenantId === tenantId
          ? { ...current, error }
          : { ...NO_WORKSPACE, tenantId, error },
      );
    };

    // A platform admin has no tenant portfolio to read; the overview is the
    // whole of their workspace.
    if (isPlatformAdmin) {
      return begin(() => client.getPlatformAdminOverview())
        .then((overview) => {
          setWorkspaceData({ ...NO_WORKSPACE, tenantId, adminOverview: overview });
        })
        .catch(failed);
    }

    return begin(() =>
      Promise.all([
        client.listAccessibleProgrammes(),
        client.listAssignedModules(),
        client.listTenantUsers(tenantId),
        client.getPortfolioSummary(),
        client.getTenantProviderSettings(),
      ]),
    )
      .then(([programmes, modules, users, portfolio, providerSettings]) => {
        setWorkspaceData({
          tenantId,
          programmes,
          modules,
          users,
          portfolio,
          disciplines: providerSettings.settings.disciplines ?? [],
          allowLeadTeamManagement:
            providerSettings.settings.allowProgrammeLeadTeamManagement !== false,
          tenantRegion: {
            locale: providerSettings.settings.locale,
            timeZone: providerSettings.settings.operatingTimeZone,
          },
          tenantProviderSettings: providerSettings.settings,
          adminOverview: null,
          error: null,
        });
      })
      .catch(failed);
  }, [client, currentTenantId, isPlatformAdmin]);

  // The reload a completed workflow wants, which is a re-read and nothing else.
  // Dropping the record first to put the spinner back up emptied every screen
  // reading this data for the length of the round trip, which reads as a full
  // page reload. The record names its own tenant and the derivations above
  // discard a stale one, so the previous one can stay up until the new one lands.
  const refreshPlatformWorkspace = fetchPlatformWorkspace;

  const textSize = platformSession?.user.textSize;
  useEffect(() => {
    applyTextSize(textSize);
    return () => {
      applyTextSize(undefined);
    };
  }, [textSize]);

  useEffect(() => {
    if (sessionStatus === "ready") {
      fetchPlatformWorkspace();
    }
  }, [fetchPlatformWorkspace, sessionStatus]);

  const handleWorkflowCompleted = React.useCallback(
    async (programmeId: string): Promise<void> => {
      await refreshPlatformWorkspace();
      if (editorSync.record?.id === programmeId) {
        await editorSync.reloadProgramme();
      }
    },
    [editorSync, refreshPlatformWorkspace],
  );

  /*
   * Where the server said, not where the ids suggest.
   *
   * Every publisher names its own destination, and rebuilding one from the
   * programme id made the client a second source of truth that disagreed with
   * the email: a revalidation reminder names `/portfolio`, and the rebuild
   * sent it to an approved programme's read-only editor. The rebuild survives
   * only for an item that names no destination at all.
   */
  const handleNotificationOpen = React.useCallback(
    (notification: NotificationFeedItem): void => {
      const { actionUrl } = notification;
      // Same origin once resolved, not merely a leading slash: `//evil.example`
      // and `/\evil.example` both resolve cross-origin, and react-router falls
      // back to `window.location.assign` when `pushState` refuses one, so the
      // in-app branch performed the very navigation the scheme check gates.
      // One that does not parse, such as `//`, names no destination at all.
      const resolved = actionUrl?.startsWith("/") ? parseInApp(actionUrl) : null;
      if (resolved?.origin === window.location.origin) {
        navigate(`${resolved.pathname}${resolved.search}${resolved.hash}`);
        return;
      }
      // Both halves of "where the server said" are read before the ids, or an
      // absolute destination on a row that also names a programme is
      // unreachable.
      if (actionUrl?.startsWith("https://")) {
        window.location.assign(actionUrl);
        return;
      }
      if (notification.programmeId) {
        if (notification.moduleId) {
          navigate(`/programmes/${notification.programmeId}/modules/${notification.moduleId}`);
          return;
        }
        // A change case is a separate document under the same programme, and
        // the feed carries its id in the event's metadata.
        const changeCaseId = notification.eventMetadata?.changeCaseId;
        const base =
          typeof changeCaseId === "string" && changeCaseId
            ? `/programmes/${notification.programmeId}/changes/${changeCaseId}`
            : `/programmes/${notification.programmeId}`;
        navigate(`${base}/overview/identity`);
      }
      // Anything else is refused. The column is a stored string, so a
      // `javascript:` one would otherwise run on click.
    },
    [navigate],
  );

  if (sessionStatus === "loading") {
    return (
      <ShellSkeleton
        pathname={pathname}
        version={__APP_VERSION__}
        label="Resolving platform session…"
        data-testid="platform-session-loading"
      />
    );
  }

  if (sessionStatus === "error" || !platformSession) {
    // The three failures reaching here — the API rejecting the caller, the API
    // being unreachable, and an unexpected error — are distinct, and reporting
    // them all as "no active tenant membership" is wrong for two of the three.
    // Classify the actual cause so the message is accurate and actionable.
    const description = describeSessionError(sessionError);
    return (
      <AuthScreen
        title={description.title}
        message={description.detail}
        data-testid="platform-session-error"
      >
        {/* Being signed in as the wrong account is the most likely reason to
            land here, so offer the switch alongside the sign-out. */}
        <div className="mt-5 flex flex-wrap gap-2">
          <Button icon="user-switch" onClick={onSwitchUser} data-testid="switch-user-denied">
            Switch user
          </Button>
          <Button icon="sign-out" onClick={onLogout}>
            Log out
          </Button>
        </div>
      </AuthScreen>
    );
  }

  const outletContext: WorkspaceOutletContext = {
    client,
    session: platformSession,
    profile: activeProfile,
    subject,
    editorSync,
    editLeasesEnabled,
    platformProgrammes: workspace.programmes,
    platformModules: workspace.modules,
    platformUsers: workspace.users,
    platformPortfolio: workspace.portfolio,
    platformDisciplines,
    tenantRegion: workspace.tenantRegion,
    tenantProviderSettings: workspace.tenantProviderSettings,
    platformAdminOverview: workspace.adminOverview,
    platformDataError: workspace.error,
    isPlatformAdmin,
    canEditProgramme,
    canEditWorkflowState,
    canEditCurrentProgramme,
    shouldRenderReadOnlyEditorView,
    canManageTenant,
    canAccessWorkspace,
    canQuickCreateProgramme,
    canQuickCreateModule,
    canManageProgrammeTeam,
    canViewReports,
    allowLeadTeamManagement,
    refreshPlatformWorkspace,
    refreshSession,
    handleWorkflowCompleted,
    ...viewMode,
  };

  // Inside a programme the surfaces take the column. A builder is given only
  // the three they reach, by their role on this programme rather than the
  // account's presentation role. Before the record lands, the programme's
  // frame rather than the tenant's, so the sidebar does not swap under them.
  let programmeSidebar: React.ReactElement | null = null;
  if (programmeShell && editorSync.record) {
    programmeSidebar = (
      <OpenProgrammeSidebar
        base={
          editorSync.record.changeCaseId
            ? `/programmes/${editorSync.record.id}/changes/${editorSync.record.changeCaseId}`
            : `/programmes/${editorSync.record.id}`
        }
        workflowState={editorSync.record.workflowState}
        version={__APP_VERSION__}
        surfaces={builderHere ? BUILDER_SURFACES : undefined}
      />
    );
  } else if (programmeShell && routeProgrammeId) {
    programmeSidebar = (
      <ProgrammeSidebarSkeleton
        base={programmeAddress(pathname)?.base ?? `/programmes/${routeProgrammeId}`}
        version={__APP_VERSION__}
      />
    );
  }

  return (
    <PermissionProvider subject={subject}>
      <TenantDisciplinesProvider disciplines={platformDisciplines}>
        <WorkspaceProvider value={outletContext}>
          <RouteTitle impersonating={Boolean(platformSession.impersonatedBy)} />
          <RegionBoundary region={region}>
            <PublishedReadinessProvider>
              <AppShell
                sidebar={
                  programmeSidebar ?? (
                    <WorkspaceSidebar
                      // A platform admin's session stands in the first tenant for
                      // want of one, which is not theirs to be named as.
                      tenantName={
                        isPlatformAdmin ? PLATFORM_SCOPE_NAME : platformSession.tenant.name
                      }
                      version={__APP_VERSION__}
                      isPlatformAdmin={isPlatformAdmin}
                      canAccessWorkspace={canAccessWorkspace}
                      canManageProgrammeTeam={canManageProgrammeTeam}
                      canManageTenant={canManageTenant}
                      canViewReports={canViewReports}
                    />
                  )
                }
                topbar={
                  <WorkspaceTopbar
                    client={client}
                    displayName={platformSession.user.displayName ?? platformSession.user.email}
                    email={platformSession.user.email}
                    accountRole={platformSession.subject.accountRole}
                    programmeCreator={
                      canQuickCreateProgramme && platformSession.subject.accountRole === "member"
                    }
                    onNotificationOpen={handleNotificationOpen}
                    onSwitchUser={onSwitchUser}
                    onLogout={onLogout}
                  />
                }
              >
                <PageContainer>
                  <RealtimeBoundary value={presence}>
                    {/* Not before the tenant's region is known: the page would
                      mount in the default format and remount when it flips,
                      losing whatever had been typed into it. */}
                    {workspaceReady ? <Outlet context={outletContext} /> : null}
                    {/* The overview is the landing screen, so it gets a skeleton
                      in its own shape rather than the generic one. */}
                    {!workspaceReady && pathname === "/" ? (
                      <OverviewLoading subject={subject} />
                    ) : null}
                    {!workspaceReady && pathname !== "/" ? (
                      <RouteContentSkeleton
                        pathname={pathname}
                        label="Loading workspace…"
                        data-testid="workspace-loading"
                      />
                    ) : null}
                  </RealtimeBoundary>
                </PageContainer>
              </AppShell>
            </PublishedReadinessProvider>
          </RegionBoundary>
        </WorkspaceProvider>
      </TenantDisciplinesProvider>
    </PermissionProvider>
  );
}
